import { RadioStationTrack, StreamingPlayerError } from '@heardis/stream-contracts';
import { useEffect, useRef, useState } from 'react';
import { useAuthContext } from '../../contexts/AuthContext';
import { useConfigContext } from '../../contexts/ConfigContext';
import { PlaybackStatus, PlayerContext, PlayerTrack, usePlayerContext } from '../../contexts/PlayerContext';
import { useSettingsContext } from '../../contexts/SettingsContext';
import { AnalyticsEventType } from '../../hooks/analytics/analytics.types';
import useAnalytics from '../../hooks/analytics/useAnalytics';
import { Logger } from '../../utils/logger';
import { getTimeInMs } from '../../utils/time';
import { fetchTracks, preloadPlayerTrack } from '../../utils/tracks';
import InfoContainer from '../InfoContainer/InfoContainer';
import { classNames } from '../../utils/styles';
import classes from './RadioStationOnDemandPlayer.module.css';
import LoadingCircle from '../../elements/icons/LoadingCircle';

const logger = new Logger('RadioStation');

export interface RadioStationQueueTrack {
  track: RadioStationTrack;
  audio: HTMLAudioElement | null;
}

export interface RadioStationOnDemandPlayerProps {
  children: React.ReactNode;
  autoplay: boolean;
}

function RadioStationOnDemandPlayer({ children, autoplay }: RadioStationOnDemandPlayerProps): JSX.Element {
  const { api, private: authEnabled, ui } = useConfigContext();
  const { cache, setError } = useSettingsContext();
  const { user } = useAuthContext();
  const { trackAnalyticsEvent } = useAnalytics();

  const player = usePlayerContext();

  const [history, setHistory] = useState<PlayerTrack[]>([]);
  const [unexpectedLoading, setUnexpectedLoading] = useState(false);

  const currentTrackRef = useRef<PlayerTrack>();
  const nextTrackRef = useRef<PlayerTrack>();

  // would be great to have a default autoplay, but this is currently inconsistent depending on the user engagement with the page
  // so we let the use start the player
  const expectedPlaybackStatus = useRef<PlaybackStatus>(autoplay ? PlaybackStatus.PLAYING : PlaybackStatus.PAUSED);
  const playTimer = useRef<NodeJS.Timeout>();
  const preloadTimer = useRef<NodeJS.Timeout>();
  const fetchTimer = useRef<NodeJS.Timeout>();

  function clearTimeouts() {
    if (playTimer?.current) clearTimeout(playTimer.current);
    if (preloadTimer?.current) clearTimeout(preloadTimer.current);
    if (fetchTimer?.current) clearTimeout(fetchTimer.current);
  }

  function play() {
    if (!player.currentTrack) return;
    const currentTimeMs = new Date().getTime();
    const startTimeMs = getTimeInMs((player.currentTrack.track as RadioStationTrack).start);
    const currentTimeOffset = currentTimeMs - startTimeMs;
    if (currentTimeOffset < 0) {
      logger.warn('something weird happend when playing a track that is not meant to be playing already', player.currentTrack);
    }
    expectedPlaybackStatus.current = PlaybackStatus.PLAYING;
    player.seek(Math.max(currentTimeOffset / 1000, 0)); // added Math.max to not have a negative position
    player.play();
  }

  function pause() {
    expectedPlaybackStatus.current = PlaybackStatus.PAUSED;
    player.pause();
  }
  function seek() {
    // seek is already disabled by `canSeek`.
    // overwrite default behavior and do nothing just in case someone would manage to trigger
  }

  function playTrack(playerTrack: PlayerTrack, forcePlay: boolean) {
    const currentTimeMs = new Date().getTime();
    const startTimeMs = getTimeInMs((playerTrack.track as RadioStationTrack).start);
    const currentTimeOffset = currentTimeMs - startTimeMs;
    player.load(playerTrack, Math.max(currentTimeOffset / 1000, 0), forcePlay); // added Math.max to not have a negative position
    currentTrackRef.current = playerTrack;
    setHistory((currState) => ([...currState, playerTrack]));
  }

  function enqueueTrack(track: RadioStationTrack) {
    logger.info('enqueue track', track);

    const currentTimeMs = new Date().getTime();
    const trackStartTimeMs = getTimeInMs(track.start);
    const timeToStart = trackStartTimeMs - currentTimeMs;
    const timeToPreload = Math.max(timeToStart - 5000, 0); // preload the audio file 5sec before the start of the track
    const timeToFetch = Math.max(timeToStart + 5000, 0); // fetch new tracks 5sec after the start of the track

    clearTimeouts();

    logger.debug('schedule preload track', { track, timeout: `${timeToPreload / 1000} secs` });
    preloadTimer.current = setTimeout(() => {
      logger.debug('trigger preload track', { track, timeout: `${timeToPreload / 1000} secs` });
      const preloadedNextTrack = preloadPlayerTrack(track);
      nextTrackRef.current = preloadedNextTrack;
    }, timeToPreload);

    logger.debug('schedule play track', { track, timeout: `${timeToStart / 1000} secs` });
    playTimer.current = setTimeout(() => {
      logger.debug('trigger play track', { track, timeout: `${timeToStart / 1000} secs` });
      if (nextTrackRef.current) playTrack(nextTrackRef.current, expectedPlaybackStatus.current === PlaybackStatus.PLAYING);
    }, timeToStart);

    logger.debug('schedule fetch tracks', { track, timeout: `${timeToFetch / 1000} secs` });
    fetchTimer.current = setTimeout(() => {
      logger.debug('trigger fetch tracks', { track, timeout: `${timeToFetch / 1000} secs` });
      fetchRadioStationTracks();
    }, timeToFetch);

    nextTrackRef.current = { track };
  }

  async function fetchRadioStationTracks(delay = 1000) {
    logger.debug('fetch tracks from backend');
    const currentReconnectDelay = delay;
    const maxReconnectDelay = 12000;
    const { tracks: [firstTrack, secondTrack], status } = await fetchTracks<RadioStationTrack>(api.endpoint, cache, authEnabled, user);
    const currentTimeMs = new Date().getTime();

    if (status.error === StreamingPlayerError.MISSING_API_ENDPOINT && currentReconnectDelay < maxReconnectDelay) {
      const newDelay = currentReconnectDelay + Math.floor(Math.random() * 3000);
      setTimeout(async () => fetchRadioStationTracks(newDelay), newDelay);
      setUnexpectedLoading(true);
      return;
    }
    if (status.error) {
      setUnexpectedLoading(false);
      setError({ code: status.error, message: 'Failed to fetch tracks.' });
      trackAnalyticsEvent({ type: AnalyticsEventType.PLAYER_ERROR, error: status.error });
    }

    setUnexpectedLoading(false);

    if (!firstTrack) {
      logger.error('radio station has no track to play');
    }

    if (!currentTrackRef.current) {
      const firstTrackStartTimeMs = getTimeInMs(firstTrack.start);
      if (firstTrackStartTimeMs <= currentTimeMs) {
        // handle first track as current track so play it and enqueue the next track
        const preloadedFirstTrack = preloadPlayerTrack(firstTrack);
        playTrack(preloadedFirstTrack, autoplay);
        enqueueTrack(secondTrack);
      } else {
        // handle first track as next track so schedule it
        logger.warn('radio station has no playing track');
        enqueueTrack(firstTrack);
      }
    } else {
      // test if the first track is the same track or raise a warning
      if (firstTrack.id === currentTrackRef.current.track.id) {
        logger.debug('ignore track because it is playing already');
      } else {
        logger.warn('a different track is expected to play now', { current: currentTrackRef.current.track, expected: firstTrack });
      }

      if (secondTrack.id === currentTrackRef.current.track.id) {
        logger.warn('there is probably a mismatch in time, current time:', new Date());
        setError({ code: StreamingPlayerError.TIME_MISMATCH, message: "time doesn't match" });
        pause();
        return;
      }

      enqueueTrack(secondTrack);
    }
  }

  useEffect(() => {
    logger.info('init');
    fetchRadioStationTracks();
  }, []);

  return (
    <PlayerContext.Provider value={{ ...player, play, pause, canSeek: false, seek }}>
      {/* <MyDebugComponent>
        <li>{`radio station expected playback status: ${expectedPlaybackStatus.current}`}</li>
        <li>
          radio station history
          <ul>
            {history.map((record) => (
              <li key={`${record.track.id}-${(record.track as RadioStationTrack).start}`}>{`${record.track.artist} - ${record.track.title}`}</li>
            ))}
          </ul>
        </li>
      </MyDebugComponent> */}
      {children}
      <div className={classNames(classes.infoContainerWrapper, unexpectedLoading ? classes.active : '')}>
        <InfoContainer
          backgroundColor={ui.page.sections.secondary.background.color}
          typography={ui.page.sections.secondary.typography}
        >
          <h2 className={classes.infoContainerTitle}>
            <LoadingCircle />
            Loading the tracks
          </h2>
          <p>
            Loading the tracks is taking longer than expected. Please wait while we are trying to load the data.
          </p>
        </InfoContainer>
      </div>
    </PlayerContext.Provider>
  );
}

export default RadioStationOnDemandPlayer;
